/* A compact guide preview opens the original film with native playback controls. */
(() => {
  if (document.documentElement.dataset.laleyGuideVideoBound) return;
  document.documentElement.dataset.laleyGuideVideoBound = 'true';
  const players = new Map();
  const previews = new Set();
  const previewBound = new WeakSet();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const bindPreviews = (root = document) => {
    root.querySelectorAll('video.pdp-clean__selfcare-how-video-thumb').forEach((video) => {
      if (previewBound.has(video)) return;
      previewBound.add(video);
      if (!('IntersectionObserver' in window)) return;
      let inView = false;
      let finished = false;
      let timer;
      let pending = false;
      const stop = () => { clearTimeout(timer); video.pause(); };
      const sync = () => {
        if (!inView || finished || reducedMotion.matches || document.hidden || [...players.values()].some(({ dialog }) => dialog.open)) { stop(); return; }
        if (pending || !video.paused) return;
        pending = true;
        const attempt = video.play();
        Promise.resolve(attempt).then(() => {
          pending = false;
          if (!inView || reducedMotion.matches || document.hidden || [...players.values()].some(({ dialog }) => dialog.open)) { stop(); return; }
          // A brief teaser communicates video without continuous unattended motion.
          timer = setTimeout(() => { finished = true; stop(); }, 4000);
        }, () => { pending = false; stop(); });
      };
      const observer = new IntersectionObserver(([entry]) => {
        inView = entry.isIntersecting && entry.intersectionRatio >= .5;
        if (!inView) finished = false;
        sync();
      }, { threshold: [.5] });
      observer.observe(video);
      previews.add({ video, stop, sync, observer });
    });
  };
  bindPreviews();
  document.addEventListener('shopify:section:load', ({ target }) => bindPreviews(target));
  reducedMotion.addEventListener('change', () => previews.forEach(({ sync }) => sync()));

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-pdp-guide-video-open]');
    if (!trigger) return;
    const guide = trigger.closest('[data-usage-guide-source="metaobject"]');
    const source = guide?.querySelector('.pdp-clean__selfcare-how-video');
    if (!source) return;

    let entry = players.get(guide);
    if (!entry) {
      const dialog = document.createElement('dialog');
      dialog.className = 'pdp-guide-video-dialog';
      dialog.setAttribute('aria-label', trigger.dataset.pdpGuideVideoLabel);
      const mediaRatio = parseFloat(source.closest('.pdp-clean__selfcare-how-media')?.style.getPropertyValue('--pdp-how-media-ratio'));
      const ratio = source.videoWidth && source.videoHeight ? source.videoWidth / source.videoHeight : mediaRatio || .75;
      dialog.style.setProperty('--pdp-guide-video-ratio', String(ratio));
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'pdp-guide-video-dialog__close';
      close.setAttribute('aria-label', trigger.dataset.pdpGuideVideoClose);
      close.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m6 6 12 12M18 6 6 18" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
      const player = source.cloneNode(true);
      player.className = 'pdp-guide-video-dialog__video';
      player.removeAttribute('aria-hidden');
      player.removeAttribute('autoplay');
      player.removeAttribute('loop');
      player.controls = true;
      player.loop = false;
      player.autoplay = false;
      player.preload = 'metadata';
      const poster = guide.querySelector('.pdp-clean__selfcare-how-video-poster');
      player.poster = poster?.currentSrc || poster?.src || '';
      player.addEventListener('loadedmetadata', () => {
        if (player.videoWidth && player.videoHeight) {
          dialog.style.setProperty('--pdp-guide-video-ratio', String(player.videoWidth / player.videoHeight));
        }
      });
      player.tabIndex = 0;
      dialog.append(close, player);
      document.body.append(dialog);
      close.addEventListener('click', () => dialog.close());
      dialog.addEventListener('close', () => {
        player.pause();
        document.documentElement.classList.remove('pdp-guide-video-is-open');
        let focusAttempts = 0;
        const restoreFocus = () => {
          if (trigger.closest('[inert]')) {
            if (focusAttempts++ < 2) requestAnimationFrame(restoreFocus);
            return;
          }
          if (trigger.getClientRects().length) trigger.focus({ preventScroll: true });
          else {
            const heading = guide.querySelector('h2');
            heading?.setAttribute('tabindex', '-1');
            heading?.focus({ preventScroll: true });
          }
        };
        requestAnimationFrame(restoreFocus);
      });
      dialog.addEventListener('click', (click) => {
        if (click.target !== dialog) return;
        const bounds = dialog.getBoundingClientRect();
        if (click.clientX < bounds.left || click.clientX > bounds.right || click.clientY < bounds.top || click.clientY > bounds.bottom) dialog.close();
      });
      entry = { dialog, player };
      players.set(guide, entry);
    }
    source.pause();
    previews.forEach(({ stop }) => stop());
    window.LaleyOverlaySystem?.captureTrigger(trigger);
    document.documentElement.classList.add('pdp-guide-video-is-open');
    entry.dialog.showModal();
    const attempt = entry.player.play();
    if (attempt?.catch) attempt.catch(() => {}); // Native controls remain available if playback is blocked.
  });

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) players.forEach(({ player }) => player.pause());
    previews.forEach(({ sync }) => sync());
  });
  window.addEventListener('pagehide', () => {
    players.forEach(({ player }) => player.pause());
    previews.forEach(({ stop }) => stop());
  });
  window.matchMedia('(min-width: 750px)').addEventListener('change', ({ matches }) => {
    if (matches) players.forEach(({ dialog }) => { if (dialog.open) dialog.close(); });
  });
  document.addEventListener('shopify:section:unload', ({ target }) => {
    previews.forEach((preview) => {
      if (!target.contains(preview.video)) return;
      preview.stop();
      preview.observer.disconnect();
      previews.delete(preview);
    });
    players.forEach(({ dialog }, guide) => {
      if (!target.contains(guide)) return;
      if (dialog.open) dialog.close();
      dialog.remove();
      players.delete(guide);
    });
  });
})();
